import React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  Pencil,
  Plus,
  Search,
  Sparkles,
  Trash2,
} from 'lucide-react';
import { APP_BTN_OUTLINE_CLASS, APP_BTN_PRIMARY_CLASS } from '../../../ui/controlStyles';
import { LoadingSpinner, InlineLoadingState } from '../../../components/LoadingSpinner';
import { SETTINGS_PATH_BUSINESS, SETTINGS_PATH_OFFERS_INVOICES } from '../../../utils/settingsRoutes';
import { chatPath } from '../../../utils/dashboardRoutes';
import {
  ROLE_EDITOR_APP_GROUPS,
  ROLE_EDITOR_WEB_GROUPS,
} from '../../settings/roleEditorGroups';

const PAGE_SIZE = 12;
const HISTORIC_PAGE_SIZE = 12;

type HistoricImportProgress = {
  phase: 'upload' | 'processing';
  uploadTotal: number;
  uploadProcessed: number;
  created: number;
  duplicates: number;
  failed: number;
  sessionDocIds: string[];
};

type MemoryFact = {
  id: string;
  user_id: string | null;
  category: string;
  category_label: string;
  key: string;
  label: string;
  value: string;
  source: string;
  source_label: string;
  scope: 'company' | 'personal';
  scope_label: string;
  editable: boolean;
  required_capability: string | null;
  source_chat_id: string | null;
  expires_at: string | null;
  updated_at: string;
};

type DraftFact = {
  category: string;
  key: string;
  label: string;
  value: string;
};

type HistoricCostBreakdown = {
  totalCostEur?: number;
  llmCostEur?: number;
  embeddingCostEur?: number;
  rerankCostEur?: number;
};

type HistoricDocument = {
  id: string;
  filename: string;
  doc_type: 'offer' | 'invoice' | 'unknown';
  doc_date: string | null;
  city: string | null;
  chunk_count: number;
  status: 'processing' | 'ready' | 'error';
  error: string | null;
  estimated_cost_eur: number;
  cost_breakdown?: HistoricCostBreakdown;
  created_at: string;
};

type HistoricBudget = {
  usedEur: number;
  capEur: number;
  warnEur: number;
  remainingEur: number;
  capReached: boolean;
  warningReached: boolean;
};

type ScopeFilter = 'all' | 'company' | 'personal';

const CAPABILITY_LABELS = new Map(
  [...ROLE_EDITOR_WEB_GROUPS, ...ROLE_EDITOR_APP_GROUPS].flatMap((group) =>
    group.perms.map((permission) => [
      permission.key,
      `${group.label}: ${permission.label}`,
    ] as const),
  ),
);

const capabilityLabel = (key: string): string =>
  CAPABILITY_LABELS.get(key) ?? key;

const formatExpiry = (value: string): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

interface MemoryFactsPanelProps {
  section: 'overview' | 'historic';
}

export function MemoryFactsPanel({ section }: MemoryFactsPanelProps) {
  const { instanceId } = useParams<{ instanceId: string }>();
  const navigate = useNavigate();
  const [adding, setAdding] = React.useState(false);
  const [facts, setFacts] = React.useState<MemoryFact[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [search, setSearch] = React.useState('');
  const [scopeFilter, setScopeFilter] = React.useState<ScopeFilter>('all');
  const [page, setPage] = React.useState(0);
  const [actionLoading, setActionLoading] = React.useState<string | null>(null);
  const [actionError, setActionError] = React.useState<string | null>(null);

  const [rawText, setRawText] = React.useState('');
  const [addScope, setAddScope] = React.useState<'company' | 'personal'>('company');
  const [drafts, setDrafts] = React.useState<DraftFact[]>([]);
  const [normalizing, setNormalizing] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [editValue, setEditValue] = React.useState('');
  const [historicDocs, setHistoricDocs] = React.useState<HistoricDocument[]>([]);
  const [historicLoading, setHistoricLoading] = React.useState(false);
  const historicInFlightRef = React.useRef(false);
  const historicHasLoadedRef = React.useRef(false);
  const [historicSearch, setHistoricSearch] = React.useState('');
  const [historicActionError, setHistoricActionError] = React.useState<string | null>(null);
  const [historicBudget, setHistoricBudget] = React.useState<HistoricBudget | null>(null);
  const [historicUploadBusy, setHistoricUploadBusy] = React.useState(false);
  const [historicDeleteId, setHistoricDeleteId] = React.useState<string | null>(null);
  const [historicPage, setHistoricPage] = React.useState(0);
  const [historicImportProgress, setHistoricImportProgress] = React.useState<HistoricImportProgress | null>(null);

  const loadHistoricDocs = React.useCallback(async () => {
    if (historicInFlightRef.current) return;
    historicInFlightRef.current = true;
    const initialLoad = !historicHasLoadedRef.current;
    if (initialLoad) {
      setHistoricLoading(true);
    }
    setHistoricActionError((prev) => (initialLoad ? null : prev));
    try {
      const res = await fetch('/api/ai/knowledge/historic-docs', { credentials: 'include' });
      const data = await res.json().catch(() => ({})) as {
        documents?: HistoricDocument[];
        budget?: HistoricBudget;
        error?: string;
      };
      if (!res.ok) throw new Error(data.error || 'Historische Dokumente konnten nicht geladen werden.');
      setHistoricDocs(data.documents ?? []);
      setHistoricBudget(data.budget ?? null);
      historicHasLoadedRef.current = true;
    } catch (e) {
      setHistoricActionError(e instanceof Error ? e.message : 'Historische Dokumente konnten nicht geladen werden.');
      if (initialLoad) {
        setHistoricDocs([]);
        setHistoricBudget(null);
      }
    } finally {
      setHistoricLoading(false);
      historicInFlightRef.current = false;
    }
  }, []);

  const loadFacts = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/ai/memory-facts', { credentials: 'include' });
      if (!res.ok) {
        const body = await res.json().catch(() => ({})) as { error?: string };
        throw new Error(body.error || 'Fakten konnten nicht geladen werden.');
      }
      const data = await res.json() as { facts?: MemoryFact[] };
      setFacts(data.facts ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Fakten konnten nicht geladen werden.');
      setFacts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    setAdding(false);
  }, [section]);

  React.useEffect(() => {
    void loadFacts();
    void loadHistoricDocs();
  }, [loadFacts, loadHistoricDocs]);

  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    return facts.filter((f) => {
      if (scopeFilter === 'company' && f.scope !== 'company') return false;
      if (scopeFilter === 'personal' && f.scope !== 'personal') return false;
      if (!q) return true;
      return (
        f.label.toLowerCase().includes(q)
        || f.value.toLowerCase().includes(q)
        || f.category_label.toLowerCase().includes(q)
        || f.source_label.toLowerCase().includes(q)
      );
    });
  }, [facts, search, scopeFilter]);

  React.useEffect(() => {
    setPage(0);
  }, [search, scopeFilter]);

  const historicFiltered = React.useMemo(() => {
    const q = historicSearch.trim().toLowerCase();
    if (!q) return historicDocs;
    return historicDocs.filter((doc) => {
      const hay = `${doc.filename} ${doc.doc_type} ${doc.city ?? ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [historicDocs, historicSearch]);

  React.useEffect(() => {
    setHistoricPage(0);
  }, [historicSearch]);

  const historicTotalPages = Math.max(1, Math.ceil(historicFiltered.length / HISTORIC_PAGE_SIZE));
  const historicSafePage = Math.min(historicPage, historicTotalPages - 1);
  const pageHistoricDocs = historicFiltered.slice(
    historicSafePage * HISTORIC_PAGE_SIZE,
    (historicSafePage + 1) * HISTORIC_PAGE_SIZE,
  );

  const historicProcessingStats = React.useMemo(() => {
    if (!historicImportProgress || historicImportProgress.phase !== 'processing') return null;
    const { sessionDocIds } = historicImportProgress;
    if (sessionDocIds.length === 0) return null;
    const sessionDocs = historicDocs.filter((doc) => sessionDocIds.includes(doc.id));
    const ready = sessionDocs.filter((doc) => doc.status === 'ready').length;
    const errors = sessionDocs.filter((doc) => doc.status === 'error').length;
    const processing = sessionDocs.filter((doc) => doc.status === 'processing').length;
    const done = ready + errors;
    const total = sessionDocIds.length;
    return {
      ready,
      errors,
      processing,
      done,
      total,
      percent: total > 0 ? Math.round((done / total) * 100) : 0,
    };
  }, [historicImportProgress, historicDocs]);

  React.useEffect(() => {
    if (!historicImportProgress || historicImportProgress.phase !== 'processing') return;
    if (!historicProcessingStats) return;
    if (historicProcessingStats.done < historicProcessingStats.total) return;
    const timeout = window.setTimeout(() => setHistoricImportProgress(null), 2500);
    return () => window.clearTimeout(timeout);
  }, [historicImportProgress, historicProcessingStats]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const pageFacts = filtered.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);

  const handleDelete = async (factId: string) => {
    setActionLoading(`${factId}:delete`);
    setActionError(null);
    try {
      const res = await fetch(`/api/ai/memory-facts/${encodeURIComponent(factId)}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({})) as { error?: string };
        throw new Error(body.error || 'Löschen fehlgeschlagen.');
      }
      await loadFacts();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Löschen fehlgeschlagen.');
    } finally {
      setActionLoading(null);
    }
  };

  const startEdit = (fact: MemoryFact) => {
    setEditingId(fact.id);
    setEditValue(fact.value);
    setActionError(null);
  };

  const saveEdit = async (factId: string) => {
    setActionLoading(`${factId}:edit`);
    setActionError(null);
    try {
      const res = await fetch(`/api/ai/memory-facts/${encodeURIComponent(factId)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ value: editValue }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({})) as { error?: string };
        throw new Error(body.error || 'Speichern fehlgeschlagen.');
      }
      setEditingId(null);
      await loadFacts();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Speichern fehlgeschlagen.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleNormalize = async () => {
    setNormalizing(true);
    setActionError(null);
    try {
      const res = await fetch('/api/ai/memory-facts/normalize', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ rawText, scope: addScope }),
      });
      const data = await res.json().catch(() => ({})) as { facts?: DraftFact[]; error?: string };
      if (!res.ok) throw new Error(data.error || 'Strukturierung fehlgeschlagen.');
      setDrafts(data.facts ?? []);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Strukturierung fehlgeschlagen.');
      setDrafts([]);
    } finally {
      setNormalizing(false);
    }
  };

  const handleSaveDrafts = async () => {
    if (!drafts.length) return;
    setSaving(true);
    setActionError(null);
    try {
      const res = await fetch('/api/ai/memory-facts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          facts: drafts.map((d) => ({
            category: d.category,
            key: d.key,
            value: d.value,
            scope: addScope,
          })),
        }),
      });
      const data = await res.json().catch(() => ({})) as { error?: string };
      if (!res.ok) throw new Error(data.error || 'Speichern fehlgeschlagen.');
      setRawText('');
      setDrafts([]);
      setAdding(false);
      await loadFacts();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  };

  const handleHistoricUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const allFiles = Array.from(files);
    const pdfFiles = allFiles.filter((file) => {
      const lowerName = file.name.toLowerCase();
      return file.type === 'application/pdf' || lowerName.endsWith('.pdf');
    });

    if (pdfFiles.length === 0) {
      setHistoricActionError('Es wurden keine PDF-Dateien gefunden.');
      return;
    }

    const BATCH_SIZE = 10;
    const batches: File[][] = [];
    for (let i = 0; i < pdfFiles.length; i += BATCH_SIZE) {
      batches.push(pdfFiles.slice(i, i + BATCH_SIZE));
    }

    setHistoricUploadBusy(true);
    setHistoricActionError(null);
    setHistoricImportProgress({
      phase: 'upload',
      uploadTotal: pdfFiles.length,
      uploadProcessed: 0,
      created: 0,
      duplicates: 0,
      failed: 0,
      sessionDocIds: [],
    });

    const sessionDocIds: string[] = [];
    let totalCreated = 0;
    let totalDuplicates = 0;
    let totalFailed = 0;
    let totalProcessed = 0;

    try {
      for (let batchIndex = 0; batchIndex < batches.length; batchIndex += 1) {
        const batch = batches[batchIndex];
        const formData = new FormData();
        batch.forEach((file) => formData.append('files', file));

        const res = await fetch('/api/ai/knowledge/historic-docs', {
          method: 'POST',
          credentials: 'include',
          body: formData,
        });
        const data = await res.json().catch(() => ({})) as {
          created?: Array<{ id: string; filename: string }>;
          duplicates?: Array<{ filename: string; reason: string }>;
          budget?: HistoricBudget;
          error?: string;
        };

        const createdCount = Array.isArray(data.created) ? data.created.length : 0;
        const duplicateCount = Array.isArray(data.duplicates) ? data.duplicates.length : 0;
        const batchTotal = batch.length;
        const failedCount = Math.max(batchTotal - createdCount - duplicateCount, 0);

        if (Array.isArray(data.created)) {
          sessionDocIds.push(...data.created.map((row) => row.id));
        }

        totalCreated += createdCount;
        totalDuplicates += duplicateCount;
        totalFailed += failedCount;
        totalProcessed += batchTotal;

        setHistoricImportProgress((prev) => ({
          phase: 'upload',
          uploadTotal: pdfFiles.length,
          uploadProcessed: totalProcessed,
          created: totalCreated,
          duplicates: totalDuplicates,
          failed: totalFailed,
          sessionDocIds: [...sessionDocIds],
        }));

        if (!res.ok) {
          setHistoricActionError(data.error || 'Upload fehlgeschlagen.');
          if (data.budget) setHistoricBudget(data.budget);
          break;
        }

        if (data.budget) setHistoricBudget(data.budget);
        if (data.budget?.capReached) {
          setHistoricActionError(`Budget cap erreicht (${data.budget.capEur.toFixed(2)} EUR). Verarbeitung gestoppt.`);
          break;
        }
      }

      await loadHistoricDocs();

      if (sessionDocIds.length > 0) {
        setHistoricImportProgress({
          phase: 'processing',
          uploadTotal: pdfFiles.length,
          uploadProcessed: pdfFiles.length,
          created: totalCreated,
          duplicates: totalDuplicates,
          failed: totalFailed,
          sessionDocIds,
        });
      } else {
        setHistoricImportProgress(null);
      }
    } catch (e) {
      setHistoricActionError(e instanceof Error ? e.message : 'Upload fehlgeschlagen.');
      if (sessionDocIds.length > 0) {
        setHistoricImportProgress({
          phase: 'processing',
          uploadTotal: pdfFiles.length,
          uploadProcessed: pdfFiles.length,
          created: totalCreated,
          duplicates: totalDuplicates,
          failed: totalFailed,
          sessionDocIds,
        });
      } else {
        setHistoricImportProgress(null);
      }
    } finally {
      setHistoricUploadBusy(false);
    }
  };

  const handleHistoricDelete = async (id: string) => {
    setHistoricDeleteId(id);
    setHistoricActionError(null);
    try {
      const res = await fetch(`/api/ai/knowledge/historic-docs/${encodeURIComponent(id)}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({})) as { budget?: HistoricBudget; error?: string };
      if (!res.ok) throw new Error(data.error || 'Löschen fehlgeschlagen.');
      if (data.budget) setHistoricBudget(data.budget);
      await loadHistoricDocs();
    } catch (e) {
      setHistoricActionError(e instanceof Error ? e.message : 'Löschen fehlgeschlagen.');
    } finally {
      setHistoricDeleteId(null);
    }
  };

  React.useEffect(() => {
    if (section !== 'historic') return;
    const importProcessing = historicImportProgress?.phase === 'processing';
    const anyProcessing = historicDocs.some((doc) => doc.status === 'processing');
    if (!importProcessing && !anyProcessing) return;

    const intervalMs = importProcessing ? 5_000 : 10_000;
    const interval = window.setInterval(() => {
      void loadHistoricDocs();
    }, intervalMs);
    return () => window.clearInterval(interval);
  }, [section, loadHistoricDocs, historicDocs, historicImportProgress]);

  const goToSettings = (path: string) => {
    if (!instanceId) return;
    navigate(`/i/${encodeURIComponent(instanceId)}${path}`);
  };

  return (
    <div>
      {actionError ? (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          {actionError}
        </div>
      ) : null}

      {section === 'overview' && !adding ? (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                <div className="relative min-w-[220px] flex-1">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-app-text-subtle" />
                  <input
                    type="search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Suchen …"
                    aria-label="Wissen suchen"
                    className="h-9 w-full rounded-md border border-app-border bg-app-surface pl-9 pr-3 text-sm text-app-text outline-none placeholder:text-app-text-subtle focus:border-pipedrive-blue focus:ring-2 focus:ring-pipedrive-blue/20"
                  />
                </div>
                <div className="inline-flex h-9 shrink-0 items-center rounded-md border border-app-border bg-app-surface p-0.5">
                  {(['all', 'company', 'personal'] as const).map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setScopeFilter(s)}
                      className={`h-8 rounded px-2.5 text-sm ${scopeFilter === s ? 'bg-app-surface-muted font-medium text-app-text' : 'text-app-text-muted hover:text-app-text'}`}
                    >
                      {s === 'all' ? 'Alle' : s === 'company' ? 'Firma' : 'Persönlich'}
                    </button>
                  ))}
                </div>
                <button type="button" className={APP_BTN_PRIMARY_CLASS} onClick={() => setAdding(true)}>
                  <Plus className="h-3.5 w-3.5" />
                  Hinzufügen
                </button>
              </div>

              {loading ? (
                <InlineLoadingState label="Laden…" className="py-10" />
              ) : error ? (
                <p className="text-sm text-red-600 py-6 text-center">{error}</p>
              ) : filtered.length === 0 ? (
                <p className="text-sm text-app-text-subtle py-8 text-center">
                  {facts.length === 0
                    ? 'Noch keine Fakten. Über Hinzufügen Wissen eintragen oder Firmendaten in den Einstellungen pflegen.'
                    : 'Keine Treffer für die Suche.'}
                </p>
              ) : (
                <>
                  <div className="overflow-hidden rounded-md border border-app-border">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-app-border bg-app-surface-muted">
                          <th className="px-3 py-2.5 text-left text-xs font-medium text-app-text-subtle">Bereich</th>
                          <th className="px-3 py-2.5 text-left text-xs font-medium text-app-text-subtle">Bezeichnung</th>
                          <th className="px-3 py-2.5 text-left text-xs font-medium text-app-text-subtle">Wert</th>
                          <th className="hidden px-3 py-2.5 text-left text-xs font-medium text-app-text-subtle sm:table-cell">Gültig für</th>
                          <th className="hidden px-3 py-2.5 text-left text-xs font-medium text-app-text-subtle md:table-cell">Quelle</th>
                          <th className="w-20 px-3 py-2.5" />
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-app-border">
                        {pageFacts.map((f) => (
                          <tr key={f.id} className="bg-app-surface hover:bg-app-hover">
                            <td className="px-3 py-2.5 text-app-text-muted whitespace-nowrap">{f.category_label}</td>
                            <td className="px-3 py-2.5 font-medium text-app-text">{f.label}</td>
                            <td className="max-w-[240px] px-3 py-2.5 text-app-text-muted">
                              {editingId === f.id ? (
                                <input
                                  type="text"
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  className="w-full border border-app-border rounded px-2 py-1 text-xs"
                                  autoFocus
                                />
                              ) : (
                                <span className="line-clamp-2" title={f.value}>{f.value}</span>
                              )}
                            </td>
                            <td className="hidden px-3 py-2.5 text-app-text-muted sm:table-cell">{f.scope_label}</td>
                            <td className="hidden px-3 py-2.5 text-app-text-subtle md:table-cell">
                              <div>{f.source_label}</div>
                              {f.required_capability ? (
                                <div
                                  className="mt-0.5 text-[10px] text-app-text-subtle"
                                  title={f.required_capability}
                                >
                                  Recht: {capabilityLabel(f.required_capability)}
                                </div>
                              ) : null}
                              {f.expires_at ? (
                                <div className="mt-0.5 text-[10px] text-app-text-subtle">
                                  Gültig bis {formatExpiry(f.expires_at)}
                                </div>
                              ) : null}
                              {f.source_chat_id && instanceId ? (
                                <Link
                                  to={chatPath(instanceId, f.source_chat_id)}
                                  className="mt-0.5 inline-flex items-center gap-1 text-[10px] text-pipedrive-blue hover:underline"
                                >
                                  Quell-Chat
                                  <ExternalLink className="h-2.5 w-2.5" />
                                </Link>
                              ) : null}
                            </td>
                            <td className="px-3 py-2.5">
                              <div className="flex items-center gap-1 justify-end">
                                {f.editable ? (
                                  editingId === f.id ? (
                                    <button
                                      type="button"
                                      disabled={actionLoading === `${f.id}:edit`}
                                      onClick={() => void saveEdit(f.id)}
                                      className="px-2 py-1 rounded text-[10px] font-medium bg-blue-100 text-blue-700 hover:bg-blue-200 disabled:opacity-50"
                                    >
                                      OK
                                    </button>
                                  ) : (
                                    <button
                                      type="button"
                                      onClick={() => startEdit(f)}
                                      className="p-1.5 rounded hover:bg-blue-100 text-slate-400 hover:text-blue-600"
                                      title="Bearbeiten"
                                    >
                                      <Pencil className="w-3.5 h-3.5" />
                                    </button>
                                  )
                                ) : null}
                                {f.editable ? (
                                  <button
                                    type="button"
                                    disabled={actionLoading === `${f.id}:delete`}
                                    onClick={() => void handleDelete(f.id)}
                                    className="p-1.5 rounded hover:bg-red-100 text-slate-400 hover:text-red-600 disabled:opacity-50"
                                    title="Löschen"
                                  >
                                    {actionLoading === `${f.id}:delete`
                                      ? <LoadingSpinner size="sm" />
                                      : <Trash2 className="w-3.5 h-3.5" />}
                                  </button>
                                ) : null}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {totalPages > 1 ? (
                    <div className="flex items-center justify-between text-xs text-app-text-subtle">
                      <span>{filtered.length} Eintrag{filtered.length !== 1 ? 'e' : ''} · Seite {safePage + 1} von {totalPages}</span>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          disabled={safePage === 0}
                          onClick={() => setPage((p) => Math.max(0, p - 1))}
                          className="p-1 rounded hover:bg-app-hover disabled:opacity-40"
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          disabled={safePage >= totalPages - 1}
                          onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                          className="p-1 rounded hover:bg-app-hover disabled:opacity-40"
                        >
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ) : null}
                </>
              )}

              <p className="text-xs leading-relaxed text-app-text-subtle">
                Firmendaten aus{' '}
                <button type="button" onClick={() => goToSettings(SETTINGS_PATH_BUSINESS)} className="text-pipedrive-blue hover:underline">
                  Geschäftsinformationen
                </button>
                {' '}und{' '}
                <button type="button" onClick={() => goToSettings(SETTINGS_PATH_OFFERS_INVOICES)} className="text-pipedrive-blue hover:underline">
                  Zahlungsbedingungen
                </button>
                {' '}werden automatisch übernommen (Quelle: Einstellungen).
              </p>
            </div>
          ) : section === 'overview' && adding ? (
            <div className="space-y-4">
              <button type="button" className={APP_BTN_OUTLINE_CLASS} onClick={() => setAdding(false)}>
                Zurück
              </button>
              <div>
                <p className="text-xs font-medium text-app-text-muted mb-2">Gültig für</p>
                <div className="inline-flex h-9 items-center rounded-md border border-app-border bg-app-surface p-0.5 text-sm">
                  <button
                    type="button"
                    onClick={() => setAddScope('company')}
                    className={`h-8 rounded px-3 ${addScope === 'company' ? 'bg-app-surface-muted font-medium text-app-text' : 'text-app-text-muted hover:text-app-text'}`}
                  >
                    Firma
                  </button>
                  <button
                    type="button"
                    onClick={() => setAddScope('personal')}
                    className={`h-8 rounded px-3 ${addScope === 'personal' ? 'bg-app-surface-muted font-medium text-app-text' : 'text-app-text-muted hover:text-app-text'}`}
                  >
                    Nur für mich
                  </button>
                </div>
              </div>

              <div>
                <label className="text-xs font-medium text-app-text-muted mb-1.5 block">
                  Freitext — die KI strukturiert daraus Fakten
                </label>
                <textarea
                  value={rawText}
                  onChange={(e) => setRawText(e.target.value)}
                  rows={5}
                  placeholder="z. B. Wir duzen Kunden und bevorzugen kurze Antworten. Standard-Zahlungsziel ist 14 Tage."
                  className="w-full text-sm border border-app-border rounded-lg px-3 py-2 outline-none focus:ring-1 focus:ring-blue-400 resize-y"
                />
              </div>

              <button
                type="button"
                disabled={normalizing || !rawText.trim()}
                onClick={() => void handleNormalize()}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {normalizing ? <LoadingSpinner size="sm" /> : <Sparkles className="w-3.5 h-3.5" />}
                Von KI strukturieren
              </button>

              {drafts.length > 0 ? (
                <section className="rounded-lg border border-app-border p-3 bg-app-surface-muted space-y-3">
                  <h3 className="text-xs font-semibold text-app-text">Vorschau — vor dem Speichern prüfen</h3>
                  <div className="space-y-2">
                    {drafts.map((d, i) => (
                      <div key={`${d.key}-${i}`} className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
                        <input
                          type="text"
                          value={d.label}
                          onChange={(e) => setDrafts((prev) => prev.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))}
                          className="border border-app-border rounded px-2 py-1.5 bg-app-surface"
                          aria-label="Bezeichnung"
                        />
                        <input
                          type="text"
                          value={d.value}
                          onChange={(e) => setDrafts((prev) => prev.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))}
                          className="sm:col-span-2 border border-app-border rounded px-2 py-1.5 bg-app-surface"
                          aria-label="Wert"
                        />
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => void handleSaveDrafts()}
                    className={APP_BTN_PRIMARY_CLASS}
                  >
                    {saving ? <LoadingSpinner size="sm" /> : <Plus className="w-3.5 h-3.5" />}
                    {drafts.length} Fakt{drafts.length === 1 ? '' : 'en'} speichern
                  </button>
                </section>
              ) : null}
            </div>
          ) : (
            <div className="space-y-4">
              {historicActionError ? (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                  {historicActionError}
                </div>
              ) : null}

              <div className="space-y-3 rounded-md border border-app-border bg-app-surface-muted p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-xs font-medium text-app-text">Upload historische Rechnungen/Angebote</p>
                  {historicBudget ? (
                    <span className={`text-[11px] font-medium ${historicBudget.capReached ? 'text-red-700' : historicBudget.warningReached ? 'text-amber-700' : 'text-app-text-muted'}`}>
                      Budget: {historicBudget.usedEur.toFixed(2)} € / {historicBudget.capEur.toFixed(2)} €
                    </span>
                  ) : null}
                </div>
                <p className="text-[11px] text-app-text-muted">
                  Names/Adressblöcke werden geschwärzt, Ort bleibt erhalten. Bei Duplikaten wird der Upload übersprungen.
                </p>
                {historicImportProgress?.phase === 'upload' ? (
                  <div className="space-y-1">
                    <div className="flex items-center justify-between text-[11px] text-app-text-muted">
                      <span>
                        Upload: {historicImportProgress.uploadProcessed} / {historicImportProgress.uploadTotal} Dateien
                      </span>
                      <span>
                        {Math.min(
                          100,
                          Math.round((historicImportProgress.uploadProcessed / historicImportProgress.uploadTotal) * 100) || 0,
                        )}
                        %
                      </span>
                    </div>
                    <div className="h-1.5 rounded-full bg-slate-200 overflow-hidden">
                      <div
                        className="h-full bg-blue-500 transition-all"
                        style={{
                          width: `${Math.min(
                            100,
                            Math.round((historicImportProgress.uploadProcessed / historicImportProgress.uploadTotal) * 100) || 0,
                          )}%`,
                        }}
                      />
                    </div>
                  </div>
                ) : null}
                {historicImportProgress?.phase === 'processing' && historicProcessingStats ? (
                  <div className="space-y-1">
                    <div className="flex items-center justify-between text-[11px] text-app-text-muted">
                      <span>
                        Verarbeitung: {historicProcessingStats.done} / {historicProcessingStats.total} Dokumente
                      </span>
                      <span>{historicProcessingStats.percent}%</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-slate-200 overflow-hidden">
                      <div
                        className="h-full bg-emerald-500 transition-all"
                        style={{ width: `${historicProcessingStats.percent}%` }}
                      />
                    </div>
                    <div className="flex flex-wrap gap-2 text-[11px] text-app-text-subtle">
                      <span>Bereit: {historicProcessingStats.ready}</span>
                      <span>In Arbeit: {historicProcessingStats.processing}</span>
                      {historicProcessingStats.errors > 0 ? (
                        <span className="text-red-600">Fehler: {historicProcessingStats.errors}</span>
                      ) : null}
                      {historicImportProgress.duplicates > 0 ? (
                        <span>Duplikate: {historicImportProgress.duplicates}</span>
                      ) : null}
                    </div>
                  </div>
                ) : null}
                <label className={`${APP_BTN_PRIMARY_CLASS} w-fit cursor-pointer`}>
                  {historicUploadBusy ? <LoadingSpinner size="sm" /> : <Plus className="w-3.5 h-3.5" />}
                  PDFs auswählen
                  <input
                    type="file"
                    accept="application/pdf"
                    multiple
                    className="hidden"
                    onChange={(e) => {
                      void handleHistoricUpload(e.target.files);
                      e.currentTarget.value = '';
                    }}
                    disabled={historicUploadBusy || Boolean(historicBudget?.capReached)}
                  />
                </label>
              </div>

              {historicLoading ? (
                <InlineLoadingState label="Laden…" className="py-8" />
              ) : historicDocs.length === 0 ? (
                <p className="text-sm text-app-text-subtle py-6 text-center">Noch keine historischen Dokumente indexiert.</p>
              ) : (
                <div className="space-y-3">
                  <div className="flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between">
                    <div className="relative min-w-[220px] flex-1">
                      <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-app-text-subtle" />
                      <input
                        type="search"
                        value={historicSearch}
                        onChange={(e) => setHistoricSearch(e.target.value)}
                        placeholder="Dokumente suchen …"
                        aria-label="Historische Dokumente suchen"
                        className="h-9 w-full rounded-md border border-app-border bg-app-surface pl-9 pr-3 text-sm text-app-text outline-none placeholder:text-app-text-subtle focus:border-pipedrive-blue focus:ring-2 focus:ring-pipedrive-blue/20"
                      />
                    </div>
                    <p className="text-[11px] text-app-text-subtle whitespace-nowrap">
                      {historicFiltered.length} von {historicDocs.length} Dokumenten
                    </p>
                  </div>

                  {historicFiltered.length === 0 ? (
                    <p className="text-sm text-app-text-subtle py-6 text-center">Keine Treffer für die Suche.</p>
                  ) : null}
                  {pageHistoricDocs.map((doc) => (
                    <div key={doc.id} className="rounded-lg border border-app-border bg-app-surface p-3 space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-semibold text-app-text line-clamp-2" title={doc.filename}>{doc.filename}</p>
                          <p className="text-[11px] text-app-text-subtle">
                            Typ: {doc.doc_type} · Ort: {doc.city || '—'} · Chunks: {doc.chunk_count} · Kosten: {(Number(doc.estimated_cost_eur || 0)).toFixed(4)} €
                          </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className={`text-[11px] px-2 py-0.5 rounded-full border whitespace-nowrap ${
                            doc.status === 'ready'
                              ? 'border-green-200 bg-green-50 text-green-700'
                              : doc.status === 'processing'
                                ? 'border-blue-200 bg-blue-50 text-blue-700'
                                : 'border-red-200 bg-red-50 text-red-700'
                          }`}>
                            {doc.status === 'ready' ? 'Bereit' : doc.status === 'processing' ? 'Verarbeitung' : 'Fehler'}
                          </span>
                          <button
                            type="button"
                            onClick={() => window.open(`/api/ai/knowledge/historic-docs/${encodeURIComponent(doc.id)}/view`, '_blank', 'noopener,noreferrer')}
                            className="p-1.5 rounded hover:bg-app-hover text-slate-400 hover:text-app-text-muted"
                            title="Geschwärzte Ansicht öffnen"
                            disabled={doc.status !== 'ready'}
                          >
                            <ExternalLink className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            disabled={historicDeleteId === doc.id}
                            onClick={() => void handleHistoricDelete(doc.id)}
                            className="p-1.5 rounded hover:bg-red-100 text-slate-400 hover:text-red-600 disabled:opacity-50"
                            title="Löschen"
                          >
                            {historicDeleteId === doc.id
                              ? <LoadingSpinner size="sm" />
                              : <Trash2 className="w-3.5 h-3.5" />}
                          </button>
                        </div>
                      </div>
                      {doc.error ? (
                        <p className="text-[11px] text-red-600">{doc.error}</p>
                      ) : null}
                      {/* cost is shown inline next to chunks */}
                    </div>
                  ))}
                  {historicTotalPages > 1 ? (
                    <div className="flex items-center justify-between text-xs text-app-text-subtle">
                      <span>
                        {historicFiltered.length} Dokument{historicFiltered.length !== 1 ? 'e' : ''} · Seite {historicSafePage + 1} von {historicTotalPages}
                      </span>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          disabled={historicSafePage === 0}
                          onClick={() => setHistoricPage((p) => Math.max(0, p - 1))}
                          className="p-1 rounded hover:bg-app-hover disabled:opacity-40"
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          disabled={historicSafePage >= historicTotalPages - 1}
                          onClick={() => setHistoricPage((p) => Math.min(historicTotalPages - 1, p + 1))}
                          className="p-1 rounded hover:bg-app-hover disabled:opacity-40"
                        >
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
      )}
    </div>
  );
}
